import {useState} from "react";
import {useAuth} from "../context/AuthContext";
import {useNavigate, Link} from "react-router-dom";
import Spinner from "../components/Spinner";

interface ReponseConnexion {
    prenom: string;
    nom: string;
    email: string;
}

const nettoyerEntree = (valeur: string): string => {
    return valeur.trim().replace(/[<>'"]/g,"").substring(0,255);
};

export default function Connexion() {
    const [email, setEmail] = useState<string>("");
    const [motDePasse, setMotDePasse] = useState<string>("");
    const [resterConnecte, setResterConnecte] = useState<boolean>(false);
    const [erreur, setErreur] = useState<string>("");
    const [chargement, setChargement] = useState<boolean>(false);
    const [nombreTentatives, setNombreTentatives] = useState<number>(0);
    const [bloqueJusqua, setBloqueJusqua] = useState<number | null>(null);

    const TENTATIVES_MAX = 5;
    const DUREE_BLOCAGE_MS = 15 * 60 * 1000;
    const naviguer = useNavigate();
    const {seConnecter} = useAuth();

    const validerFormulaire = (): boolean => {
        if (!email || !motDePasse) { setErreur("Veuillez remplir tous les champs"); return false; }
        if (!email.includes("@")) { setErreur("Veuillez entrer un email valide"); return false; }
        if (motDePasse.length < 6) { setErreur("Le mot de passe doit contenir au moins 6 caractères"); return false; }
        return true;
    };

    const gererConnexion = async (): Promise<void> => {
        if (bloqueJusqua && Date.now() < bloqueJusqua) {
            const minutesRestantes = Math.ceil((bloqueJusqua - Date.now()) / 60000);
            setErreur(`Trop de tentatives. Réessayez dans ${minutesRestantes} minute(s).`);
            return;
        }
        if (!validerFormulaire()) return;
        setErreur("");
        setChargement(true);
        try {
            const reponse = await fetch(
                `${process.env.REACT_APP_API_URL}/api/auth/connexion`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify({email, motDePasse, resterConnecte})
                }
            );
            if (!reponse.ok) {
                const nouvellesTentatives = nombreTentatives + 1;
                setNombreTentatives(nouvellesTentatives);
                if (nouvellesTentatives >= TENTATIVES_MAX) {
                    setBloqueJusqua(Date.now() + DUREE_BLOCAGE_MS);
                    setErreur("Trop de tentatives échouées. Compte bloqué pendant 15 minutes.");
                    return;
                }
                let messageErreur = `Email ou mot de passe incorrect. ${TENTATIVES_MAX - nouvellesTentatives} tentative(s) restante(s).`;
                try { const donnees = await reponse.json(); if (donnees.message) messageErreur = donnees.message; } catch (e) {}
                setErreur(messageErreur);
                return;
            }
            setNombreTentatives(0);
            setBloqueJusqua(null);
            const donnees: ReponseConnexion = await reponse.json();
            seConnecter(donnees.prenom, donnees.nom, donnees.email);
            naviguer("/tableau");
        } catch (e) {
            setErreur("Impossible de contacter le serveur. Vérifiez votre connexion");
        } finally {
            setChargement(false);
        }
    };

    const gererToucheEntree = (e: React.KeyboardEvent): void => {
        if (e.key === "Enter") gererConnexion();
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 gap-6"
            style={{background: "linear-gradient(135deg, #6B21A8 0%, #9333EA 40%, #F97316 100%)"}}
            onKeyDown={gererToucheEntree}>
            <div className="absolute top-0 left-0 w-72 h-72 rounded-full opacity-20"
                style={{background: "radial-gradient(circle, #F97316, transparent)", transform: "translate(-30%, -30%)"}}/>
            <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-20"
                style={{background: "radial-gradient(circle, #6B21A8, transparent)", transform: "translate(30%, 30%)"}}/>
            <div className="text-center relative z-10">
                <h2 className="text-white text-3xl font-bold tracking-wide drop-shadow-lg">Bienvenue sur GMITIX</h2>
                <p className="text-white/70 text-sm mt-2">Votre assistant de réunions intelligent</p>
            </div>
            <div className="card w-full max-w-md bg-white shadow-2xl rounded-3xl relative z-10">
                <div className="card-body p-8">
                    <div className="flex justify-center mb-6">
                        <div className="px-8 py-3 rounded-2xl text-white font-bold text-2xl tracking-wide shadow-lg"
                            style={{background: "linear-gradient(90deg, #6B21A8, #F97316)"}}>GMITIX</div>
                    </div>
                    <p className="text-center text-gray-400 text-sm mb-6">Connectez-vous à votre espace JuniorISEP</p>
                    {erreur && <div className="alert alert-error mb-4 rounded-xl"><span className="text-sm">{erreur}</span></div>}
                    <div className="form-control mb-3">
                        <label className="label pb-1"><span className="label-text font-medium text-gray-700">Email</span></label>
                        <input type="email" placeholder="votre@email.com"
                            className="input input-bordered w-full rounded-xl border-2 focus:border-purple-500 focus:outline-none transition-all"
                            autoFocus value={email} onChange={(e) => setEmail(nettoyerEntree(e.target.value))}
                            autoComplete="email" disabled={chargement}/>
                    </div>
                    <div className="form-control mb-4">
                        <label className="label pb-1"><span className="label-text font-medium text-gray-700">Mot de passe</span></label>
                        <input type="password" placeholder="••••••••"
                            className="input input-bordered w-full rounded-xl border-2 focus:border-purple-500 focus:outline-none transition-all"
                            value={motDePasse} onChange={(e) => setMotDePasse(nettoyerEntree(e.target.value))}
                            autoComplete="current-password" disabled={chargement}/>
                    </div>
                    <div className="form-control mb-6">
                        <label className="label cursor-pointer justify-start gap-3">
                            <input type="checkbox" className="checkbox checkbox-primary rounded-lg"
                                checked={resterConnecte} onChange={(e) => setResterConnecte(e.target.checked)} disabled={chargement}/>
                            <span className="label-text text-gray-600">Rester connecté pendant 1 mois</span>
                        </label>
                    </div>
                    <button className="w-full py-3 rounded-xl text-white font-semibold text-base transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center"
                        style={{background: "linear-gradient(90deg, #6B21A8, #F97316)"}}
                        onClick={gererConnexion} disabled={chargement || !email || !motDePasse}>
                        {chargement ? <Spinner size="sm"/> : "Se connecter"}
                    </button>
                    <p className="text-center text-sm text-gray-400 mt-4">
                        Pas encore de compte ?{" "}
                        <Link to="/inscription" className="font-semibold text-purple-600 hover:text-orange-500 transition-colors">S'inscrire</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}